import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getAccessToken,
  clearAccessToken,
  submitFragebogen,
  getUserAnswers,
} from "../utils/fetchFunctions";
import Button from "../components/Button";
import FormError from "../components/FormError";
import { LEVEL_COLORS, LEVELS } from "../utils/levelColors";

const DIMENSION_DESCRIPTIONS = {
  "Data Collection Process": "The processes relating to the capture of data from multiple sources for secondary purposes.",
  "Data Management - Governance": "The level of maturity of the data management processes and governance.",
  "Data Management - Infrastructure": "The level of implementation and development of the data holder's data management infrastructure.",
  "Data Provenance": "Clear description of the lineage of the dataset providing transparent and comprehensive documentation of the data pipeline.",
  "Data Access": "How well defined and implemented are data access processes, from a legal, ethical and technical perspective.",
  "Data Analytics Environment": "Analytical services, tooling and access to secure data environments.",
  "Data Enhancement - Augmentation": "The application of various techniques to make data more useable for specific purposes.",
  "Data Enhancement - Enrichment": "Data sources enriched with annotations, image labels, phenomes, derivations, NLP derived data labels.",
  "Data Model": "Availability of a clear, documented data model that provides structure and standardisation.",
  "Data Dictionary": "Provided documented data dictionary and terminologies.",
};

const LevelCircle = ({ level, achievedLevel }) => {
  const isActive = level === achievedLevel;
  const isPast = level < achievedLevel;
  const colors = LEVEL_COLORS[level];

  return (
    <div
      className={[
        "dim-level-circle",
        isActive ? "dim-level-circle--active" : "",
        isPast ? "dim-level-circle--past" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        isActive
          ? { backgroundColor: colors.bg, borderColor: colors.bg }
          : isPast
          ? { borderColor: colors.bg }
          : {}
      }
      title={`Level ${level}${isActive ? " — achieved" : ""}`}
    />
  );
};

const MaturityOverview = () => {
  const [levelProgress, setLevelProgress] = useState(null);
  const [overallLevel, setOverallLevel] = useState(null);
  const [isAllDimensionsComplete, setIsAllDimensionsComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdatedDate, setLastUpdatedDate] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate("/login");
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        const answersData = await getUserAnswers();
        const userHasAnswers = answersData && answersData.length > 0;

        if (userHasAnswers) {
          const latestAnswer = answersData.reduce((latest, current) => {
            const currentDate = new Date(current.updated_at);
            const latestDate = latest ? new Date(latest.updated_at) : null;
            return !latestDate || currentDate > latestDate ? current : latest;
          }, null);
          if (latestAnswer) setLastUpdatedDate(new Date(latestAnswer.updated_at));
        }

        if (location.state?.levelProgress) {
          setLevelProgress(location.state.levelProgress);
          setOverallLevel(location.state.overallLevel ?? 0);
          setIsAllDimensionsComplete(location.state.isAllDimensionsComplete || false);
        } else if (userHasAnswers) {
          const result = await submitFragebogen();
          setLevelProgress(result.levelProgress);
          setOverallLevel(result.overallLevel ?? 0);
          setIsAllDimensionsComplete(result.isAllDimensionsComplete || false);
        }
      } catch (err) {
        if (err.message && err.message.includes("401")) {
          clearAccessToken();
          navigate("/login");
          return;
        }
        setError(err.message || "Error loading data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    navigate,
    location.state?.levelProgress,
    location.state?.overallLevel,
    location.state?.isAllDimensionsComplete,
  ]);

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  };

  const computeOverallScore = () => {
    if (!levelProgress) return null;
    const dims = Object.values(levelProgress);
    if (!dims.length) return 0;
    return dims.reduce((sum, d) => sum + d.achievedLevel, 0) / dims.length;
  };

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard…</div>;
  }

  if (error && !levelProgress) {
    return (
      <>
        <FormError error={error} />
        <Button onClick={() => window.location.reload()} className="auth-button">
          Try again
        </Button>
      </>
    );
  }

  const overallScore = computeOverallScore();
  const dimCount = levelProgress ? Object.keys(levelProgress).length : 10;

  return (
    <>
      {/* ── Page header ────────────────────────────── */}
      <div className="dashboard-header">
        <h1 className="dashboard-title">Maturity Dashboard</h1>
        {lastUpdatedDate && (
          <p className="dashboard-subtitle">Last updated: {formatDate(lastUpdatedDate)}</p>
        )}
      </div>

      {error && <FormError error={error} />}

      {/* ── Overall score hero card ─────────────────── */}
      {overallLevel !== null && levelProgress && (
        <div className="dashboard-overall-card">
          <div className="dashboard-overall-grid">
            <div className="dashboard-overall-section">
              <div className="dashboard-overall-label">OVERALL MATURITY SCORE</div>
              <div className="dashboard-overall-desc">
                Add up all dimension levels and divide by {dimCount}.
              </div>
              <div className="dashboard-overall-value">{overallScore?.toFixed(2)}</div>
            </div>
            <div className="dashboard-overall-section dashboard-overall-section--right">
              <div className="dashboard-overall-label">YOUR OVERALL MATURITY LEVEL</div>
              <div className="dashboard-overall-desc">
                Round the result down to the nearest whole number.
              </div>
              <div
                className="dashboard-overall-level-badge"
                style={{ backgroundColor: LEVEL_COLORS[overallLevel]?.bg ?? "#555" }}
              >
                Level {overallLevel}
              </div>
            </div>
          </div>
          <div className="dashboard-level-scale">
            {LEVELS.map((l) => (
              <div
                key={l}
                className={`dashboard-scale-item ${l === overallLevel ? "dashboard-scale-item--active" : ""}`}
                style={{
                  backgroundColor: LEVEL_COLORS[l].bg,
                  opacity: l <= overallLevel ? 1 : 0.25,
                }}
              >
                Level {l}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Dimension table ─────────────────────────── */}
      {levelProgress && (
        <div className="dashboard-table-section">
          <h2 className="dashboard-section-title">Maturity Level by Dimension</h2>
          <p className="dashboard-table-note">
            For each dimension, the filled circle indicates your achieved maturity level.
          </p>
          <div className="dashboard-table-wrapper">
            <table className="dashboard-dim-table">
              <thead>
                <tr>
                  <th className="dim-th-name">Dimension</th>
                  {LEVELS.map((l) => (
                    <th
                      key={l}
                      className="dim-th-level"
                      style={{ backgroundColor: LEVEL_COLORS[l].bg }}
                    >
                      Level {l}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(levelProgress).map(([dimension, data], idx) => {
                  const { score, maxScore, achievedLevel } = data;
                  const dimScore = maxScore > 0 ? (score / (maxScore / 5)).toFixed(1) : "—";
                  return (
                    <tr
                      key={dimension}
                      className={`dim-row ${idx % 2 === 0 ? "dim-row--even" : "dim-row--odd"}`}
                    >
                      <td className="dim-td-name">
                        <div className="dim-name">{dimension}</div>
                        <div className="dim-description">
                          {DIMENSION_DESCRIPTIONS[dimension] ?? ""}
                        </div>
                        <div className="dim-score-row">
                          <span
                            className="dim-level-pill"
                            style={{
                              backgroundColor: LEVEL_COLORS[achievedLevel]?.light,
                              color: LEVEL_COLORS[achievedLevel]?.bg,
                              border: `1px solid ${LEVEL_COLORS[achievedLevel]?.bg}`,
                            }}
                          >
                            Level {achievedLevel}
                          </span>
                          <span className="dim-pts">
                            {score} / {maxScore} pts · Score {dimScore}
                          </span>
                        </div>
                      </td>
                      {LEVELS.map((l) => (
                        <td key={l} className="dim-td-level">
                          <LevelCircle level={l} achievedLevel={achievedLevel} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Next steps ──────────────────────────────── */}
      {!isAllDimensionsComplete && (
        <div className="dashboard-next-steps">
          <h2 className="dashboard-section-title">Next steps</h2>
          <p className="dashboard-next-steps-text">
            You haven't completed all the questions yet. Update your answers in the questionnaire to see your final maturity level.
          </p>
          <Button onClick={() => navigate("/fragenbogen")} className="auth-button">
            Update questionnaire
          </Button>
        </div>
      )}
    </>
  );
};

export default MaturityOverview;
